import React, { useEffect } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from "react-router-dom";
import { makeStyles, Theme } from "@material-ui/core";
import { KButton } from "@kyburz/kdesign-system";
import { useDispatch } from "react-redux";

import useSetState from "../../../hooks/useTypedSetState";
import { VehicleTemplateColumn } from "../typings/VehicleTemplateColumn";
import { ChangableVehicleTemplate } from "../facades/VehicleTemplate";
import { OdometerSource } from "../typings/RawVehicleTemplate";
import { useCreateVehicleTemplates } from "../requests/CreateVehicleTemplate";
import { setAlert } from "../../../redux/actions/actions";

import BaseLayout from "../../../components/layouts/BaseLayout";
import CompaniesTab from "../components/tabs/CompaniesTab";
import EditingTab from "../components/tabs/EditingTab";

const useStyles = makeStyles(({ palette }: Theme) => ({
  layout: {
    height: "calc(100vh - 72px)",
    boxSizing: "border-box",
  },
  main: {
    height: "calc(100vh - 72px - 72px - 24px)",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  buttons: {
    width: "100%",
    height: 72,
    display: "flex",
    justifyContent: "flex-end",
    position: "fixed",
    padding: "16px 48px 0 0",
    bottom: 0,
    right: 0,
    borderTop: `1px solid ${palette.grey[300]}`,
    background: "white",
    gap: 16,
  },
}));

const VehicleTemplateCreating: React.FC = () => {
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();

  const match = useRouteMatch({
    path: "/config/vehicle/templates/templates/create/:tab",
    strict: true,
    sensitive: true,
  });

  const { tab } = match?.params as any;

  const [{ activeTab }, setState] = useSetState({ activeTab: tab });

  useEffect(() => {
    setState({ activeTab: tab });
  }, [setState, tab]);

  const handleTabClick = (_, newValue) => {
    history.push(`/config/vehicle/templates/templates/create/${newValue}`);
  };

  const { loading: saveLoading, request: createTemplate } =
    useCreateVehicleTemplates();

  const [template, setTemplate] = useSetState<ChangableVehicleTemplate>({
    name: "",
    description: "",
    isDefaultTemplate: false,
    ignitionValue: "",
    columns: [] as VehicleTemplateColumn[],
    tags: [] as string[],
    companies: [] as string[],
    owner: "",
    displayColumns: null as string[] | null,
    errorCodesMappingName: null as string | null,
    internalTariffId: null as string | null,
    odometerSource: OdometerSource.Can,
  });

  const goBack = () => {
    history.push("/config/vehicle/templates/templates/");
  };

  const onSave = async () => {
    try {
      await createTemplate({ template });
      goBack();
    } catch (e) {
      dispatch(
        setAlert(`Failed to create template! ${(e as any)?.details}`, "error"),
      );
    }
  };

  return (
    <BaseLayout
      title="Create New Vehicle Template"
      handleArrowClick={goBack}
      activeTab={activeTab}
      tabs={[
        { label: "Template Editor", value: "editor" },
        { label: "Companies", value: "companies" },
      ]}
      handleTabChange={handleTabClick}
      mainClassname={classes.layout}
    >
      <div className={classes.main}>
        <Switch>
          <Route path="/config/vehicle/templates/templates/create/editor" exact>
            <EditingTab template={template} onChangeTemplate={setTemplate} />
          </Route>
          <Route path="/config/vehicle/templates/templates/create/companies">
            <CompaniesTab
              title={`Vehicle template: ${template.name}`}
              selectedCompanies={template.companies}
              onCompaniesChange={(companies) => setTemplate({ companies })}
            />
          </Route>
          <Redirect to="/config/vehicle/templates/templates/create/editor" />
        </Switch>
      </div>

      <footer className={classes.buttons}>
        <KButton children="Cancel" theme="secondary" onClick={goBack} />

        <KButton
          children="Save and close"
          onClick={onSave}
          loading={saveLoading}
          disabled={!template?.name || !template.internalTariffId}
        />
      </footer>
    </BaseLayout>
  );
};

export default VehicleTemplateCreating;
